import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSelector, useDispatch } from 'react-redux';
import { FontFamily } from '../../../constants/fonts';
import { setAnnouncementBannerDismissed } from '../../../store/slices/homescreenSlice';
import { useTheme } from '../../../theme';

const InfoIcon = ({ color = '#1a3a4a' }: { color?: string }) => (
  <Svg width="20" height="20" viewBox="0 0 24 24" fill={color}>
    <Path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </Svg>
);

const CloseIcon = ({ color = '#1A2B3A' }: { color?: string }) => (
  <Svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <Path
      d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
      fill={color}
    />
  </Svg>
);

// const WarnIcon = () => (
//   <Svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//     <Path
//       d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
//       stroke="#FFFFFF"
//       strokeWidth="1.8"
//       strokeLinejoin="round"
//     />
//     <Path d="M12 9v5" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" />
//     <Circle cx="12" cy="17" r="1" fill="#FFFFFF" />
//   </Svg>
// );

/**
 * Split "Label: rest of message" into { label, body }.
 * Strips leading emoji and whitespace from the label portion.
 * Returns { label: null, body: message } if no colon is found.
 */
const parseMessage = (message: string): { label: string | null; body: string } => {
  // Avoid splitting on URL colons (http:// https://)
  const colonIndex = message.indexOf(':');
  if (colonIndex !== -1 && colonIndex < 60) {
    const rawLabel = message.slice(0, colonIndex).trim();
    const body = message.slice(colonIndex + 1).trim();
    if (rawLabel.length > 0 && body.length > 0) {
      return { label: rawLabel, body };
    }
  }
  return { label: null, body: message.trim() };
};

const AnnouncementBanner: React.FC = () => {
  const dispatch = useDispatch();
  const dismissed = useSelector((state: any) => state.homescreen.announcementBannerDismissed);
  const announcementBannerData = useSelector((state: any) => state.homescreen.announcementBannerData);
  const activeBanners: Array<{ id: number | string; message: string }> = announcementBannerData?.data ?? [];
  const activeBanner = activeBanners[0] ?? null;
  const isWeb = Platform.OS === 'web';
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  if (!activeBanner || dismissed) return null;

  const { label, body } = parseMessage(activeBanner.message);

  // Dark-mode overrides — only applied when isDark is true; light mode uses
  // the static StyleSheet values exclusively (nothing below changes in light mode).
  const iconColor = isDark ? colors.text.primary : '#1a3a4a';
  const textColor = isDark ? colors.text.primary : undefined;
  const labelColor = isDark ? colors.text.primary : undefined;

  return (
    <View style={[styles.container, isDark && { backgroundColor: colors.statusBackground.info }]}>
      <View style={styles.leading}>
        <View style={styles.infoIcon}>
          <InfoIcon color={iconColor} />
        </View>
        {/* <View style={styles.warnIcon}> */}
          {/* <WarnIcon /> */}
        {/* </View> */}
        <Text style={[styles.messageText, isWeb ? styles.messageTextWeb : styles.messageTextMobile, isDark && { color: textColor }]}>
          {label ? <Text style={[styles.label, isDark && { color: labelColor }]}>{label}: </Text> : null}
          {body}
        </Text>
      </View>
      <TouchableOpacity
        style={[
          isWeb ? styles.dismissButton : styles.closeButton,
          isWeb && isDark && { backgroundColor: colors.background.elevated },
        ]}
        onPress={() => dispatch(setAnnouncementBannerDismissed(true))}
        activeOpacity={0.8}
        accessibilityRole="button"
        accessibilityLabel="Dismiss announcement"
      >
        {isWeb
          ? <Text style={[styles.dismissText, isDark && { color: colors.text.primary }]}>Dismiss</Text>
          : <CloseIcon color={iconColor} />
        }
      </TouchableOpacity>
    </View>
  );
};

export default AnnouncementBanner;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#add8e6',
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 12,
  },
  leading: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  infoIcon: {
    opacity: 0.9,
    marginTop: 2,
  },
  // warnIcon: {
  //   opacity: 0.9,
  // },
  messageText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    color: '#1a3a4a',
    flexShrink: 1,
    fontFamily: FontFamily.regular,
  },
  messageTextWeb: {
    textAlign: 'center',
  },
  messageTextMobile: {
    textAlign: 'center',
  },
  label: {
    fontWeight: '700',
    fontFamily: FontFamily.bold,
    color: '#1a3a4a',
  },
  dismissButton: {
    backgroundColor: '#1A2B3A',
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 4,
    marginLeft: 8,
    alignSelf: 'center',
  },
  closeButton: {
    width: 28,
    height: 28,
    marginLeft: 4,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  dismissText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
    fontFamily: FontFamily.medium,
  },
});

